import { Box, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreVertOutlined,
  PrintOutlined,
} from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useRef, useState } from "react"

import { TransferActionType } from "../../constants"
import { isTransferWorkOrderCreated } from "../../utils"

import { type Transfer, TransferAs400SyncStatus, TransferStatus } from "@/graphql/codegen/graphql"

const TRANSFER_STATUS_TO_ACTIONS: Record<TransferStatus, TransferActionType[]> = {
  [TransferStatus.Draft]: [TransferActionType.Edit, TransferActionType.Cancel],
  [TransferStatus.PendingPickAndPutaway]: [
    TransferActionType.Edit,
    TransferActionType.WorkOrderConfirm,
    TransferActionType.WorkOrderPrint,
    TransferActionType.Cancel,
    TransferActionType.PalletLabelPrint,
  ],
  [TransferStatus.Completed]: [TransferActionType.PalletLabelPrint],
  [TransferStatus.Cancelled]: [],
}

const AS400_SYNC_PENDING_STATUSES = new Set<TransferAs400SyncStatus>([
  TransferAs400SyncStatus.WorkOrderUpserting,
  TransferAs400SyncStatus.WorkOrderConfirming,
  TransferAs400SyncStatus.WorkOrderDeleting,
])

interface TransferRowActionsProps {
  transfer: Transfer
  isPrinting: boolean
  onEdit: (transfer: Transfer) => void
  onCancel: (transfer: Transfer) => void
  onConfirmWorkOrder: (transfer: Transfer) => void
  onPrintWorkOrder: (transfer: Transfer) => void
  onPalletLabelPrintAll: (transfer: Transfer) => void
}

export const TransferRowActions: FC<TransferRowActionsProps> = ({
  transfer,
  isPrinting,
  onEdit,
  onCancel,
  onConfirmWorkOrder,
  onPrintWorkOrder,
  onPalletLabelPrintAll,
}) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const actions = TRANSFER_STATUS_TO_ACTIONS[transfer.status]
  const isSyncPending = !!transfer.as400SyncStatus && AS400_SYNC_PENDING_STATUSES.has(transfer.as400SyncStatus)
  const hasWorkOrder = isTransferWorkOrderCreated(transfer)

  const handleMenuClose = () => setIsMenuOpen(false)

  const handlePrintWorkOrder = () => {
    handleMenuClose()
    onPrintWorkOrder(transfer)
  }

  const handleEdit = () => {
    handleMenuClose()
    onEdit(transfer)
  }

  const handleCancel = () => {
    handleMenuClose()
    onCancel(transfer)
  }

  const handleConfirmWorkOrder = () => {
    handleMenuClose()
    onConfirmWorkOrder(transfer)
  }

  const handlePalletLabelPrintAll = () => {
    handleMenuClose()
    onPalletLabelPrintAll(transfer)
  }

  if (!actions.length) return null

  return (
    <Box ref={anchorElRef}>
      <IconButton size='small' onClick={() => setIsMenuOpen(true)} data-testid='transfer.row-actions.menu-button'>
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isMenuOpen}
        onClose={handleMenuClose}
        data-testid='transfer.row-actions.menu'
      >
        {actions.includes(TransferActionType.Edit) && (
          <MenuItem onClick={handleEdit} disabled={isSyncPending} data-testid='transfer.row-actions.edit-menu-item'>
            <ListItemIcon>
              <EditOutlined />
            </ListItemIcon>
            <ListItemText primary='Edit Transfer' />
          </MenuItem>
        )}
        {actions.includes(TransferActionType.WorkOrderConfirm) && hasWorkOrder && (
          <MenuItem
            onClick={handleConfirmWorkOrder}
            disabled={isSyncPending}
            data-testid='transfer.row-actions.confirm-work-order-menu-item'
          >
            <ListItemIcon>
              <CheckOutlined />
            </ListItemIcon>
            <ListItemText primary='Confirm Work Order' />
          </MenuItem>
        )}
        {actions.includes(TransferActionType.WorkOrderPrint) && hasWorkOrder && (
          <MenuItem onClick={handlePrintWorkOrder} data-testid='transfer.row-actions.print-work-order-menu-item'>
            <ListItemIcon>
              <PrintOutlined />
            </ListItemIcon>
            <ListItemText primary='Print Work Order' />
          </MenuItem>
        )}
        {actions.includes(TransferActionType.Cancel) && (
          <MenuItem onClick={handleCancel} disabled={isSyncPending} data-testid='transfer.row-actions.cancel-menu-item'>
            <ListItemIcon>
              <DeleteOutlined />
            </ListItemIcon>
            <ListItemText primary='Cancel Transfer' />
          </MenuItem>
        )}
        {actions.includes(TransferActionType.PalletLabelPrint) && hasWorkOrder && (
          <>
            {actions.length > 1 && <Divider />}
            <MenuItem
              onClick={handlePalletLabelPrintAll}
              disabled={isPrinting}
              data-testid='transfer.row-actions.print-pallet-label-menu-item'
            >
              <ListItemIcon>
                <PrintOutlined />
              </ListItemIcon>
              <ListItemText primary='Print All QR Codes' />
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  )
}
